import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import { useMutation } from '@tanstack/react-query';
import styles from 'assets/styles/modal.module.scss';
import LoadingButton from 'components/loadingButton';
import RiveResult from 'components/riveEmpty';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ErrorResponse } from 'interfaces/global.types';
import { toast } from 'react-toastify';
import policyService from 'services/policy';
import { columns } from './column';
import root from './styles.module.scss';

type Props = {
  excelData: any;
  prev: () => void;
  close: () => void;
  refetch: () => void;
  setExcelData: (e: any) => void;
};

export default function ExcelTable({ prev, close, refetch, excelData, setExcelData }: Props) {
  dayjs.extend(customParseFormat);
  const { isLoading: addLoad, mutate } = useMutation({
    mutationFn: (data: any) => policyService.import(data),
    onSuccess: () => {
      toast.success(`Полис добавлена`);
      close();
      refetch();
    },
    onError: (error: ErrorResponse) => {
      const data: any = error.data;

      const customObject = excelData.map((i: any) => {
        const hasError = data.errors.some((e: any) => e.policy_number === i.policy_number);
        const successPolicy = data.success.includes(i.policy_number);
        return {
          ...i,
          status: hasError ? 'active' : successPolicy ? 'in_active' : null,
          statusMsg: data.errors.map((j: any) =>
            j.policy_number === i.policy_number ? j.msg : null,
          ),
        };
      });

      setExcelData(customObject);
    },
  });

  const formSubmit = () => {
  const params = excelData.map((i: any) => {
    const parseDate = (dateStr: any) => {
      if (!dateStr) return null;
      const d = dayjs(dateStr, 'DD/MM/YYYY');
      return d.isValid() ? d.format('DD/MM/YYYY') : dateStr;
    };

    return {
      ...i,
      id: undefined,
      birth_date: parseDate(i.birth_date),
      policy_end_date: parseDate(i.policy_end_date),
      policy_start_date: parseDate(i.policy_start_date),
    };
  });

  mutate(params);
};

  return (
    <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
      <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 650 }} size='small' aria-label='import table'>
          <TableHead>
            <TableRow>
              {columns.map((i: any) => (
                <TableCell
                  key={i.id}
                  sx={{
                    right: '0',
                    fontSize: '14px',
                    color: '#000000',
                    fontWeight: '600',
                    minWidth: i.width,
                    position: i.position,
                    backgroundColor: '#ffffff',
                  }}>
                  {i.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {excelData?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns?.length ?? 1} sx={{ border: 'none', p: 0 }}>
                  <RiveResult />
                </TableCell>
              </TableRow>
            ) : (
              excelData?.map((item: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{
                    '.MuiTableCell-body': {
                      border: 'none',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#3f3f3f',
                      padding: '10px 20px',
                      backgroundColor: 'white',
                    },
                  }}>
                  {columns.map((column: any) =>
                    column.field === 'status' ? (
                      <Tooltip
                        key={column.id ?? column.field}
                        title={<span className={root.tooltip}>{item.statusMsg}</span>}>
                        <TableCell
                          sx={{
                            right: '0',
                            position: column.position,
                            boxShadow: '0 0 0 rgba(0,0,0,.5)',
                          }}>
                          {column?.renderCell ? column?.renderCell(item) : item[column?.field]}
                        </TableCell>
                      </Tooltip>
                    ) : (
                      <TableCell
                        key={column.id ?? column.field}
                        sx={{
                          right: '0',
                          position: column.position,
                        }}>
                        {column?.renderCell ? column?.renderCell(item) : item[column?.field]}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        className={styles.modalFooter}
        sx={{ position: 'sticky', bottom: 0, zIndex: 2, borderTop: '1px solid #eee' }}>
        <LoadingButton variant='cancel' onClick={prev}>
          Предыдущий
        </LoadingButton>
        <LoadingButton loading={addLoad} onClick={formSubmit} type='submit' variant='send'>
          Подтвердить
        </LoadingButton>
      </Box>
    </Box>
  );
}
